import { isValid, parse, parseISO, set } from 'date-fns';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// feature driver-cards-v2
import { cloneTimelineLoad } from './timeline';
import {
  TIMELINE_EVENT_KIND_DELIVERY,
  EDIT_COMMAND_EDIT_STOP_DETAILS,
  EDIT_COMMAND_EDIT_STOP_APPOINTMENTS,
} from './constants';
//////////////////////////////////////////////////

const getEventAt = (event, source) => {
  const fields = event.kind === GC.EVENT_TYPE_PICKUP
    ? [GC.FIELD_LOAD_EVENT_EARLY_DATE, GC.FIELD_EARLY_DATE]
    : [
      GC.FIELD_LOAD_EVENT_LATE_DATE,
      GC.FIELD_LATE_DATE,
      GC.FIELD_LOAD_EVENT_EARLY_DATE,
      GC.FIELD_EARLY_DATE,
    ];

  const value = fields.reduce((date, field) => date || source[field], null);
  const epoch = new Date(value).getTime();

  return Number.isFinite(epoch) ? value : event.at;
};

const isSameEventTime = (left, right) => {
  const leftEpoch = new Date(left).getTime();
  const rightEpoch = new Date(right).getTime();

  if (Number.isFinite(leftEpoch) && Number.isFinite(rightEpoch)) {
    return leftEpoch === rightEpoch;
  }

  return left === right;
};

const getEpoch = (value) => {
  if (!value) return null;

  const epoch = new Date(value).getTime();

  if (Number.isFinite(epoch)) return epoch;

  const parsed = parse(String(value), GC.DEFAULT_DATE_TIME_FORMAT, new Date());

  return isValid(parsed) ? parsed.getTime() : null;
};

const getAppointmentDay = (value) => {
  if (!value) return null;

  if (value instanceof Date) return isValid(value) ? value : null;

  const text = String(value);

  const day = /^\d{4}-\d{2}-\d{2}/.test(text)
    ? parseISO(text)
    : parse(text, GC.DEFAULT_DATE_FORMAT, new Date());

  if (isValid(day)) return day;

  const fallback = new Date(value);

  return isValid(fallback) ? fallback : null;
};

const getAppointmentTime = (day, value) => {
  if (!day || !value) return null;

  if (typeof value === 'object') {
    const hour = Number(value.hour);
    const minute = Number(value.minute || 0);

    return Number.isInteger(hour) && hour >= 0 && hour <= 23
      && Number.isInteger(minute) && minute >= 0 && minute <= 59
      ? set(day, { hours: hour, minutes: minute, seconds: 0, milliseconds: 0 }).getTime()
      : null;
  }

  const formats = [GC.DEFAULT_TIME_FORMAT, GC.DEFAULT_MILITARY_TIME_FORMAT, 'HH:mm:ss', 'h:mm:ss a'];

  const parsed = formats
    .map((format) => parse(String(value).trim(), format, day))
    .find(isValid);

  return parsed ? parsed.getTime() : null;
};

const getAppointmentWindow = (event, load) => {
  const source = event.source || {};

  const editedAppointment = load.draftChanges
    && load.draftChanges.events
    && load.draftChanges.events[event.id]
    && load.draftChanges.events[event.id].appointments;

  const day = getAppointmentDay(source[GC.FIELD_LOAD_APPOINTMENT_DATE]);

  const earlyAt = getAppointmentTime(day, source[GC.FIELD_LOAD_APPOINTMENT_EARLY_TIME])
    || (!editedAppointment && getEpoch(source[GC.FIELD_APPOINTMENT_EARLY_DATE]));

  const lateAt = getAppointmentTime(day, source[GC.FIELD_LOAD_APPOINTMENT_LATE_TIME])
    || (!editedAppointment && getEpoch(source[GC.FIELD_APPOINTMENT_LATE_DATE]));

  if (!Number.isFinite(earlyAt) && !Number.isFinite(lateAt)) return null;

  const startAt = Number.isFinite(earlyAt) ? earlyAt : lateAt;
  const endAt = Number.isFinite(lateAt) ? lateAt : earlyAt;

  return {
    endAt,
    startAt,
    anchorAt: event.kind === GC.EVENT_TYPE_PICKUP ? startAt : endAt,
  };
};

const getAppointmentPlacementConflict = (load, eventIndex, appointment) => {
  const events = load.events || [];
  const { startAt, endAt } = appointment;

  if (endAt < startAt) return { reason: 'INVALID_WINDOW' };

  const overlappingEvent = events.find((event, index) => {
    if (index === eventIndex) return false;

    const otherAt = getEpoch(event.at);
    const otherAppointment = getAppointmentWindow(event, load);

    const otherStartAt = otherAppointment && otherAppointment.endAt >= otherAppointment.startAt
      ? otherAppointment.startAt
      : otherAt;

    const otherEndAt = otherAppointment && otherAppointment.endAt >= otherAppointment.startAt
      ? otherAppointment.endAt
      : otherAt;

    return Number.isFinite(otherStartAt)
      && startAt <= otherEndAt
      && endAt >= otherStartAt;
  });

  if (overlappingEvent) return { reason: 'OVERLAP', otherEventId: overlappingEvent.id };

  const previous = events[eventIndex - 1];
  const next = events[eventIndex + 1];

  if (previous && startAt < getEpoch(previous.at)) {
    return { reason: 'ORDER', otherEventId: previous.id };
  }

  if (next && endAt > getEpoch(next.at)) {
    return { reason: 'ORDER', otherEventId: next.id };
  }

  return null;
};

export const getAppointmentConflicts = (load) => (load.events || []).reduce((conflicts, event, index) => {
  const appointment = getAppointmentWindow(event, load);

  if (!appointment) return conflicts;

  const placementConflict = getAppointmentPlacementConflict(load, index, appointment);
  const plannedAt = getEpoch(event.at);

  const conflict = placementConflict || (
    plannedAt !== null && (plannedAt < appointment.startAt || plannedAt > appointment.endAt)
      ? { reason: 'UNALIGNED' }
      : null
  );

  return conflict
    ? [...conflicts, {
      ...conflict,
      eventId: event.id,
      appointmentAt: appointment.anchorAt,
      appointmentEndAt: appointment.endAt,
      appointmentStartAt: appointment.startAt,
    }]
    : conflicts;
}, []);

export const getAppointmentConflictMessage = (load, conflict) => {
  const otherEvent = (load.events || []).find(({ id }) => id === conflict.otherEventId);

  const otherLabel = otherEvent
    ? `${otherEvent.kind === GC.EVENT_TYPE_PICKUP ? 'P' : 'D'}${otherEvent.sequence}`
    : '';

  const messages = {
    INVALID_WINDOW: G.getWindowLocale('messages:appointment-window-invalid', 'Appointment end is before its start.'),
    OVERLAP: `${G.getWindowLocale('messages:appointment-overlaps-stop', 'Appointment overlaps stop')} ${otherLabel}.`,
    ORDER: `${G.getWindowLocale(
      'messages:appointment-changes-stop-order',
      'Appointment conflicts with stop order near',
    )} ${otherLabel}.`,
    UNALIGNED: G.getWindowLocale(
      'messages:appointment-not-aligned-with-stop',
      'Appointment differs from the planned stop time. The stop stays in place.',
    ),
  };

  return messages[conflict.reason] || G.getWindowLocale(
    'messages:appointment-needs-schedule-review',
    'Appointment needs a schedule review.',
  );
};

const getDraftChanges = (load, eventId, changeType, values) => {
  const draftChanges = load.draftChanges || {};
  const events = draftChanges.events || {};
  const eventChanges = events[eventId] || {};

  return {
    ...draftChanges,
    events: {
      ...events,
      [eventId]: {
        ...eventChanges,
        [changeType]: values,
      },
    },
  };
};

const createDraftStopCommand = ({
  load,
  kind,
  values,
  eventId,
  changeType,
  updateTime,
  originalEvent,
}) => {
  const eventIndex = (load.events || []).findIndex(({ id }) => id === eventId);

  if (eventIndex < 0) return null;

  const before = cloneTimelineLoad(load);
  const after = cloneTimelineLoad(load);
  const event = after.events[eventIndex];
  const currentSource = event.source || {};

  const sourceValues = {
    ...currentSource,
    ...values,
  };

  const source = {
    ...sourceValues,
    location: values.location
      ? { ...(currentSource.location || {}), ...values.location }
      : currentSource.location,
  };

  const sourceAt = getEventAt(event, originalEvent || currentSource);
  const submittedAt = getEventAt(event, source);
  const timeChanged = updateTime && !isSameEventTime(sourceAt, submittedAt);
  const at = timeChanged ? submittedAt : event.at;

  after.events[eventIndex] = {
    ...event,
    at,
    source,
    location: source.location || event.location,
  };
  after.draftChanges = getDraftChanges(load, eventId, changeType, values);

  const firstPickupIndex = after.events.findIndex(
    ({ kind: eventKind }) => eventKind === GC.EVENT_TYPE_PICKUP,
  );

  const finalDeliveryIndex = after.events.reduce((result, { kind: eventKind }, index) => (
    eventKind === TIMELINE_EVENT_KIND_DELIVERY ? index : result
  ), -1);

  if (timeChanged && eventIndex === firstPickupIndex) after.startAt = at;

  if (timeChanged && eventIndex === finalDeliveryIndex) after.endAt = at;

  return {
    kind,
    after,
    before,
    eventId,
    loadId: load.id,
  };
};

export const createEditDraftStopCommand = (load, eventId, submittedValues, originalEvent) => (
  createDraftStopCommand({
    load,
    eventId,
    originalEvent,
    updateTime: true,
    values: submittedValues,
    changeType: GC.FIELD_STOP,
    kind: EDIT_COMMAND_EDIT_STOP_DETAILS,
  })
);

export const createEditDraftStopAppointmentsCommand = (load, eventId, submittedValues) => (
  createDraftStopCommand({
    load,
    eventId,
    updateTime: false,
    values: submittedValues,
    changeType: 'appointments',
    kind: EDIT_COMMAND_EDIT_STOP_APPOINTMENTS,
  })
);
